<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import ArrowsAiAssistantsAPI from 'dashboard/api/arrowsAi/assistants';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import MetricCard from 'dashboard/components-next/arrowsAi/MetricCard.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import InboxBanner from 'dashboard/components-next/arrowsAi/overview/InboxBanner.vue';
import QuickLinks from 'dashboard/components-next/arrowsAi/overview/QuickLinks.vue';

const emptyMetrics = () => ({
  handled: 0,
  auto_resolution: 0,
  handoff: 0,
  reopen: 0,
  hours_saved: 0,
  depth: 0,
});

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant, canAssignInbox } =
  useArrowsAiAssistants();
const stats = ref(emptyMetrics());

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const formatRate = value => `${Number(value || 0)}%`;

const metrics = computed(() => [
  {
    key: 'handled',
    label: t('ARROWS_AI.OVERVIEW.METRICS.HANDLED.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.HANDLED.HINT'),
    value: String(stats.value.handled || 0),
  },
  {
    key: 'autoResolution',
    label: t('ARROWS_AI.OVERVIEW.METRICS.AUTO_RESOLUTION.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.AUTO_RESOLUTION.HINT'),
    value: formatRate(stats.value.auto_resolution),
  },
  {
    key: 'handoff',
    label: t('ARROWS_AI.OVERVIEW.METRICS.HANDOFF.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.HANDOFF.HINT'),
    value: formatRate(stats.value.handoff),
  },
  {
    key: 'reopen',
    label: t('ARROWS_AI.OVERVIEW.METRICS.REOPEN.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.REOPEN.HINT'),
    value: formatRate(stats.value.reopen),
  },
  {
    key: 'hoursSaved',
    label: t('ARROWS_AI.OVERVIEW.METRICS.HOURS_SAVED.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.HOURS_SAVED.HINT'),
    value: `${stats.value.hours_saved || 0}h`,
  },
  {
    key: 'depth',
    label: t('ARROWS_AI.OVERVIEW.METRICS.DEPTH.LABEL'),
    hint: t('ARROWS_AI.OVERVIEW.METRICS.DEPTH.HINT'),
    value: String(stats.value.depth || 0),
  },
]);

const loadMetrics = async id => {
  if (!id) {
    stats.value = emptyMetrics();
    return;
  }

  try {
    const { data } = await ArrowsAiAssistantsAPI.getMetrics(id);
    stats.value = { ...emptyMetrics(), ...data };
  } catch (error) {
    stats.value = emptyMetrics();
    const message = parseAPIErrorResponse(error);
    useAlert(
      typeof message === 'string'
        ? message
        : t('ARROWS_AI.OVERVIEW.FETCH_ERROR')
    );
  }
};

watch(
  () => assistant.value?.id,
  id => loadMetrics(id),
  { immediate: true }
);
</script>

<template>
  <PageLayout :header-title="t('ARROWS_AI.OVERVIEW.TITLE')">
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-6 pb-8">
      <InboxBanner
        :inbox-count="assistant.inboxIds?.length || 0"
        :locked="!canAssignInbox"
      />
      <p class="text-sm text-n-slate-11">{{ assistant.description }}</p>
      <p class="text-sm text-n-slate-11">{{ t('ARROWS_AI.OVERVIEW.RANGE') }}</p>
      <div
        class="grid grid-cols-1 overflow-hidden border rounded-xl sm:grid-cols-2 lg:grid-cols-3 bg-n-weak border-n-weak gap-px"
      >
        <MetricCard
          v-for="metric in metrics"
          :key="metric.key"
          :label="metric.label"
          :value="metric.value"
          :hint="metric.hint"
        />
      </div>
      <QuickLinks />
    </div>
  </PageLayout>
</template>
